import { motion } from 'framer-motion';
import React, { FC, Fragment, useState } from 'react';
import {
    getMe,
    getMySingleDoctorId,
    getSingleDoctor,
    isSingleDoctorAccess,
} from '../../store/idbHandler';
import Hotkeys from 'react-hot-keys';
import { track } from '../../tracking';
import { useDispatch } from 'react-redux';
import { toggleGoogleReviewDialog } from '../../store/actions/session';
import { CopyShareComponent, ImageComponent, NameComponent } from './NewProfileComponents';
import { getAclViewClassName } from '../../restrictions/utils';
import { SREV2 } from '../../api/pushGenie';

export const GMB_VIDEO = 'GMB_VIDEO';

const DashboardInviteCodePanel: React.FC<{
    withInviteCode?: boolean;
    withInviteLink?: boolean;
    downloadExcel?: () => void;
}> = ({ withInviteLink = true, withInviteCode = true, downloadExcel }) => {
    const dispatch = useDispatch();
    const me = getMe();
    const doc = getSingleDoctor();

    let dName = `${(doc
        ? [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
        : me?.docs?.[0] && isSingleDoctorAccess()
        ? [me?.docs?.[0].personal.name.f, me?.docs?.[0].personal.name.l]
        : []
    )
        .filter(Boolean)
        .join(' ')}`.trim();

    if (dName.trim()) {
        dName = `Dr. ${dName}`;
    }

    if (!dName && me) {
        dName = `${me.user.fn}`;
    }

    const imgUrl =
        doc?.profile.personal.pic ||
        (isSingleDoctorAccess() && me?.docs?.[0]?.personal?.pic
            ? me?.docs?.[0]?.personal?.pic
            : `https://a.eka.care/user-avatar/${me?.user.oid}`);

    const message = `${dName} has invited you to eka.care. Use invite code ${doc?.profile.professional.invite?.invite_code} or click here ${doc?.profile.professional.invite?.invite_link} to consult with ${dName}. A connected healthcare experience awaits you on eka.care.`;
    const inviteCode = doc?.profile.professional.invite?.invite_code;
    const inviteLink = doc?.profile.professional.invite?.invite_link;

    const showInviteCode = inviteCode && withInviteCode;
    const showInviteLink = inviteLink && withInviteLink;
    return (
        <div className="p-8 w-full flex flex-col items-end">
            <div className="flex flex-col xl:flex-row xl:space-x-16 xl:space-y-0 space-y-16 xl:items-center">
                <ImageComponent imgUrl={imgUrl} />
                <div>
                    <NameComponent dName={dName} />
                    {showInviteCode && (
                        <p
                            className={`text-neutral-1 text-13 ${getAclViewClassName(
                                SREV2.INVITE_CODE,
                            )}`}
                        >
                            Invite Code: <span className="font-700 select-all">{inviteCode}</span>
                        </p>
                    )}
                    {showInviteLink && (
                        <p
                            className={`text-neutral-1 text-13 ${getAclViewClassName(
                                SREV2.INVITE_CODE,
                            )}`}
                        >
                            Invite Link:{' '}
                            <a className="underline text-actionPrimary select-all">{inviteLink}</a>
                        </p>
                    )}
                    <CopyShareComponent
                        showInviteCode={showInviteCode}
                        showInviteLink={showInviteLink}
                        message={message}
                    />
                    {!doc?.profile.professional.googleReviewLink &&
                        isSingleDoctorAccess() &&
                        getMySingleDoctorId() && (
                            <div
                                className={`flex items-center space-x-4 ${
                                    showInviteCode && showInviteLink ? 'mt-12' : 'mt-4'
                                }`}
                            >
                                <div className="flex items-start space-x-4">
                                    <div className="flex items-center justify-center w-14 h-16">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="w-14 h-14 fill-darwin-accent-medical-history-orange-800"
                                        >
                                            <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z" />
                                        </svg>
                                    </div>
                                    <p className="body-3 text-darwin-neutral-900">
                                        <span>Setup</span>
                                        <span>
                                            <button
                                                onClick={() => {
                                                    dispatch(
                                                        toggleGoogleReviewDialog(
                                                            getMySingleDoctorId(),
                                                        ),
                                                    );
                                                }}
                                                className="ml-4 inline focus:outline-none font-700 text-12 text-darwin-primary-500 underline"
                                            >
                                                Google Review
                                            </button>
                                        </span>
                                    </p>
                                </div>
                            </div>
                        )}
                    {downloadExcel && (
                        <div
                            className={`flex items-center space-x-4 ${
                                showInviteCode && showInviteLink ? 'mt-12' : 'mt-4'
                            }`}
                        >
                            <div className="flex items-start space-x-4">
                                <div className="flex items-center justify-center w-14 h-16">
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M14.5625 2.625L8.5625 0.125C8.40625 0.0625 8.15625 0.03125 8 0.03125C7.8125 0.03125 7.5625 0.0625 7.40625 0.125L1.40625 2.625C0.84375 2.875 0.5 3.40625 0.5 4C0.5 12.0625 6.40625 16 7.96875 16C9.59375 16 15.5 12 15.5 4C15.5 3.40625 15.125 2.875 14.5625 2.625ZM11 6.25C11 6.4375 10.9375 6.625 10.8125 6.75L7.8125 10.25C7.59375 10.5 7.3125 10.5 7.25 10.5C7.03125 10.5 6.84375 10.4062 6.71875 10.2812L5.21875 8.78125C5.0625 8.65625 5 8.46875 5 8.25C5 7.84375 5.3125 7.5 5.75 7.5C5.9375 7.5 6.125 7.5625 6.25 7.71875L7.1875 8.625L9.65625 5.75C9.8125 5.5625 10.0312 5.5 10.25 5.5C10.8125 5.5 11 6.03125 11 6.25Z"
                                            fill="#004BA8"
                                        />
                                    </svg>
                                </div>
                                <p className="body-3 text-darwin-neutral-900">
                                    <span>Download Your</span>
                                    <span>
                                        <button
                                            onClick={downloadExcel}
                                            className="ml-4 inline focus:outline-none font-700 text-12 text-darwin-primary-500 underline"
                                        >
                                            Patient Database
                                        </button>
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardInviteCodePanel;

export const GoogleReviewDialog: FC = () => {
    const [isInterested, setIsInterested] = useState(false);
    const [isGmbSubmitted, setIsGmbSubmitted] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [gmbLink, setGmbLink] = useState('');
    const dispatch = useDispatch();
    const close = () => {
        dispatch(toggleGoogleReviewDialog(undefined));
    };

    const playVideo = () => {
        const el = document.getElementById(GMB_VIDEO) as HTMLVideoElement;
        setShowVideo(true);
        el?.play();
    };

    const onDocInterested = () => {
        setIsInterested(true);
        track('DOC_INTERESTED', { interested: 'true' });
    };

    const onGmbSubmit = () => {
        if (!gmbLink) {
            return;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            $crisp.push(['do', 'message:send', ['text', `Attach my review link : ${gmbLink}`]]);
        } catch (error) {
            console.error(error);
        }
        track('DOC_GMB', { link: gmbLink });
        setIsGmbSubmitted(true);
    };

    const onGmbChange = (e: React.ChangeEvent<HTMLInputElement>) => setGmbLink(e.target.value);

    return (
        <Hotkeys keyName="esc" onKeyDown={close}>
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 20 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-lato"
            >
                <div className="modal-overlay" onClick={close}></div>
                <div className="modal-content w-60p h-3/4 flex flex-col">
                    <div className="flex items-center justify-between p-16 rounded-t-4 bg-neutral-9">
                        <p className="text-13 leading-5 bold text-neutral-1">Google Reviews</p>
                        <div
                            onClick={close}
                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="w-16 h-16 fill-neutral-1"
                            >
                                <path d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex items-start w-full p-24 flex-1 overflow-auto">
                        <div className="w-60p space-y-24">
                            <p className="body-1 text-darwin-neutral-1000">
                                To collect Google Reviews from patients you need a Google My
                                Business Profile page.
                            </p>
                            <div className="p-8">
                                <div className="flex items-start space-x-8">
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="w-14 h-14 fill-darwin-accent-green-900"
                                        >
                                            <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z" />
                                        </svg>
                                    </div>
                                    <div className="space-y-8">
                                        <p className="bold body-1">
                                            If you have a Google My Business Profile
                                        </p>
                                        <div className="space-y-16">
                                            <p className="body-1 text-darwin-neutral-800">
                                                Find your GMB profile link by following the
                                                instructions in this{' '}
                                                <span
                                                    onClick={playVideo}
                                                    className="underline text-darwin-primary-500 bold cursor-pointer"
                                                >
                                                    video
                                                </span>
                                            </p>
                                            <div className="flex items-center space-x-10 rounded-4 border border-darwin-neutral-100 px-8">
                                                <div className="w-16 h-16 flex items-center justify-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 640 512"
                                                        className="w-16 h-16 fill-darwin-neutral-500"
                                                    >
                                                        <path d="M172.5 131.1C228.1 75.51 320.5 75.51 376.1 131.1C426.1 181.1 433.5 260.8 392.4 318.3L391.3 319.9C381 334.2 361 337.6 346.7 327.3C332.3 317 328.9 297 339.2 282.7L340.3 281.1C363.2 249 359.6 205.1 331.7 177.2C300.3 145.8 249.2 145.8 217.7 177.2L105.5 289.5C73.99 320.1 73.99 372 105.5 403.5C133.3 431.4 177.3 435 209.3 412.1L210.9 410.1C225.3 400.7 245.3 404 255.5 418.4C265.8 432.8 262.5 452.8 248.1 463.1L246.5 464.2C188.1 505.3 110.2 498.7 60.21 448.8C3.741 392.3 3.741 300.7 60.21 244.3L172.5 131.1zM467.5 380C411 436.5 319.5 436.5 263 380C213 330 206.5 251.2 247.6 193.7L248.7 192.1C258.1 177.8 278.1 174.4 293.3 184.7C307.7 194.1 311.1 214.1 300.8 229.3L299.7 230.9C276.8 262.1 280.4 306.9 308.3 334.8C339.7 366.2 390.8 366.2 422.3 334.8L534.5 222.5C566 191 566 139.1 534.5 108.5C506.7 80.63 462.7 76.99 430.7 99.9L429.1 101C414.7 111.3 394.7 107.1 384.5 93.58C374.2 79.2 377.5 59.21 391.9 48.94L393.5 47.82C451 6.731 529.8 13.25 579.8 63.24C636.3 119.7 636.3 211.3 579.8 267.7L467.5 380z" />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    onChange={onGmbChange}
                                                    className="placeholder-darwin-neutral-400 text-darwin-neutral-black body-1 py-10 w-full"
                                                    placeholder="Enter or paste GMB link here"
                                                />
                                            </div>
                                            <div className="flex items-center space-x-16">
                                                <button
                                                    disabled={!gmbLink}
                                                    onClick={onGmbSubmit}
                                                    className="primary-btn btn--small px-16 py-8"
                                                >
                                                    {isGmbSubmitted ? 'Submitted' : 'Submit'}
                                                </button>
                                                {isGmbSubmitted && (
                                                    <p className="text-darwin-neutral-700">
                                                        Your Review Link will be activated within 24
                                                        hours.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-8">
                                <div className="flex items-start space-x-8">
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                            className="fill-darwin-neutral-600 w-14 h-14"
                                        >
                                            <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM175 208.1L222.1 255.1L175 303C165.7 312.4 165.7 327.6 175 336.1C184.4 346.3 199.6 346.3 208.1 336.1L255.1 289.9L303 336.1C312.4 346.3 327.6 346.3 336.1 336.1C346.3 327.6 346.3 312.4 336.1 303L289.9 255.1L336.1 208.1C346.3 199.6 346.3 184.4 336.1 175C327.6 165.7 312.4 165.7 303 175L255.1 222.1L208.1 175C199.6 165.7 184.4 165.7 175 175C165.7 184.4 165.7 199.6 175 208.1V208.1z" />
                                        </svg>
                                    </div>
                                    <div className="space-y-16">
                                        <div className="space-y-4">
                                            <p className="bold body-1">
                                                If you don’t have a Google My Business Profile
                                            </p>
                                            <p className="body-1 text-darwin-neutral-800">
                                                If you don’t have a GMB profile, contact your Eka
                                                Relationship Manager. We can set it up for you for
                                                free
                                            </p>
                                        </div>
                                        <button
                                            onClick={onDocInterested}
                                            className="px-16 py-8 btn--small secondary-btn"
                                        >
                                            {!isInterested ? `I'm interested` : `Request Submitted`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="w-40p flex flex-col items-center justify-center h-full">
                            <div className="h-full relative">
                                {showVideo ? (
                                    <video
                                        id={GMB_VIDEO}
                                        style={{ height: '100%' }}
                                        controls
                                        autoPlay
                                    >
                                        <source src="/videos/gmb.mp4" type="video/mp4" />
                                    </video>
                                ) : (
                                    <Fragment>
                                        <img src="/images/google-review.png" className="h-full" />
                                        <div
                                            onClick={playVideo}
                                            className="left-1/2 top-1/2 transform -translate-y-1/2 -translate-x-1/2 absolute w-64 h-64 flex items-center justify-center cursor-pointer"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                className="w-64 h-64 hover:fill-darwin-primary-500 fill-darwin-neutral-500"
                                            >
                                                <path d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM176 168V344C176 352.7 180.7 360.7 188.3 364.9C195.8 369.2 205.1 369 212.5 364.5L356.5 276.5C363.6 272.1 368 264.4 368 256C368 247.6 363.6 239.9 356.5 235.5L212.5 147.5C205.1 142.1 195.8 142.8 188.3 147.1C180.7 151.3 176 159.3 176 168V168z" />
                                            </svg>
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                            <p className="body-3 text-darwin-neutral-700 mt-8">
                                Example Google Business Profile
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </Hotkeys>
    );
};
